import { action, computed, makeObservable, observable } from "mobx";
import { FileUpload, UploadStatus } from "./FileUpload";
import { groupBy } from "lodash";
import { PatientToUpload } from "./PatientToUpload";
import JSZip from "jszip";
import { notNullOrUndefined } from "../util/functions/notNullOrUndefined";
import { nullOrUndefined } from "../util/functions/nullOrUndefined";
import type { IUploadCodeAndSourceSelectionStore } from "./content/upload-code-and-source-selection/UploadCodeAndSourceSelectionStore";
import type { IPatientUploadCompleteCallbackStore } from "./PatientUploadCompleteCallbackStore";
import type { IDicomUploadTusFingerprintStore } from "./DicomUploadTusFingerprintStore";
import type { StudyInUpload } from "./StudyInUpload";
import { isNullOrWhiteSpace } from "../util/functions/isNullOrWhiteSpace";
import type { ICurrentUploadSettingsStore } from "./CurrentUploadSettingsStore";
import { delay } from "../util/functions/delay";

export interface IUploadStore {
    filesNotUploadable: FileUpload[];
    patientsFound: PatientToUpload[];
    numberOfSelectedFiles: number;
    numberOfCheckingFiles: number;
    numberOfDICOMFiles: number;
    numberOfNonDICOMFiles: number;
    preventInputOfNewFiles: boolean;
    allUploadsDone: boolean;
    somethingIsUploading: boolean;
    maxUploadFilesAtOnce: number;
    maxNonDicomFilesAddedToAStudy: number;
    supportedNonDICOMFilesToArrange: FileUpload[];
    allStudies: StudyInUpload[];
    allStudiesThatAreNotUploadedYet: StudyInUpload[];
    selectedNonDICOMFiles: FileUpload[];
    reachedMaxUploadSize: boolean;
    inputChanged: boolean;

    addSelectedFiles(files: File[]): Promise<void>;
    resetFiles(): void;
    removePatientToUpload(patientToUpload: PatientToUpload): void;
    removeFileUpload(file: FileUpload): void;
    addAllSelectedNonDICOMFilesToArrangeToStudy(study: StudyInUpload): void;
    removeAllSelectedNonDICOMFiles(): void;
}

export class UploadStore implements IUploadStore {
    private readonly endpoint = "/POW4/files";

    public readonly maxNonDicomFilesAddedToAStudy = 10;

    public maxUploadFilesAtOnce = 4;

    @observable
    public inputChanged: boolean = false;

    constructor(
        private readonly uploadCodeAndSourceSelectionStore: IUploadCodeAndSourceSelectionStore,
        private readonly patientUploadCompleteCallbackStore: IPatientUploadCompleteCallbackStore,
        private readonly dicomUploadTusFingerprintStore: IDicomUploadTusFingerprintStore,
        private readonly currentUploadSettingsStore: ICurrentUploadSettingsStore,
    ) {
        makeObservable(this);
    }

    @action
    private setInputChanged(value: boolean) {
        this.inputChanged = value;
    }

    @action
    public async addSelectedFiles(files: File[], overrulePreventInput = false): Promise<void> {
        if (this.preventInputOfNewFiles && !overrulePreventInput) return;

        this.setInputChanged(true);
        await delay(10); //needed to be able to rerender based on the changed inputChanged property, because adding a large amount of files might freeze the browser for a moment
        await this.addFiles(files);
        await this.checkFilesToCheck();
        this.checkPatientDataUploadableFiles();
        this.checkToArrangeSupportedNonDICOMFiles();
        this.setInputChanged(false);
    }

    @action
    private async addFiles(files: File[]): Promise<void> {
        //Read files
        for (let i = files.length - 1; i >= 0; i--) {
            //Check if zip file
            if (files[i].name.endsWith(".zip")) {
                await this.readZipFile(files[i]);
            }
            //Not a zip file, continue
            else {
                const fileUpload = this.makeNewSingleFileUpload(files[i]);
                this.addFileToCheck(fileUpload);
            }
        }
    }

    private async readZipFile(file: File): Promise<void> {
        try {
            const zip = new JSZip();

            //Load files in zip
            const loadedZipFile = await zip.loadAsync(file);

            //Wait for unzip of zipfiles and filter out directories (null) or files that were unable to unzip
            const waitForUnzip = Object.keys(loadedZipFile.files)
                .map(fileName => zip.file(fileName))
                .filter(notNullOrUndefined)
                .map(async file => {
                    try {
                        const blob = await file.async("blob");
                        return new File([blob], file.name);
                    } catch (e) {
                        console.error(`❌ Could not unzip ${file.name}.`, e);
                        throw e;
                    }
                });

            //We got all the files from the zip, try to add them again
            const files = await Promise.all(waitForUnzip);
            return this.addSelectedFiles(files, true);
        } catch (e) {
            //Incorrect zip file, move to notUploadable!
            console.error(e, file);
            const fileUpload = this.makeNewSingleFileUpload(file);
            fileUpload.setFileError(true);
            fileUpload.setChecking(false);
            this.addFileToNotUploadable(fileUpload);
        }
    }

    private async checkFilesToCheck(): Promise<void> {
        //check and await each of the files to check to determine whether or not they are uploadable
        for (let i = this.filesToCheck.length - 1; i >= 0; i--) {
            const fileUpload = this.filesToCheck[i];
            await fileUpload.checkAndMakeFileUploadReady();
            this.removeFileFromFilesToCheck(i);

            if (fileUpload.uploadable) {
                this.addFileToUploadable(fileUpload);
            } else {
                this.addFileToNotUploadable(fileUpload);
            }
        }
    }

    @action
    private checkPatientDataUploadableFiles(): void {
        const groupedFiles = groupBy(this.filesUploadable, f => f.patientId);

        this.filesUploadable = [];

        for (const key in groupedFiles) {
            //Dicom files
            if (key === "") {
                //Supported non-dicom files
                for (let i = groupedFiles[key].length - 1; i >= 0; i--) {
                    const file = groupedFiles[key][i];
                    if (!this.supportedNonDICOMFilesToArrange.some(f => f.file.name === file.file.name && f.file.size === file.file.size)) {
                        this.supportedNonDICOMFilesToArrange.push(groupedFiles[key][i]);
                    }
                }
            } else {
                const files = groupedFiles[key];
                const patientAlreadyExists = this.patientsFound.find(p => p.patientId === key);
                if (patientAlreadyExists) {
                    patientAlreadyExists.addFiles(files);
                } else {
                    this.patientsFound.push(
                        new PatientToUpload(files, this, this.patientUploadCompleteCallbackStore, this.uploadCodeAndSourceSelectionStore, this.dicomUploadTusFingerprintStore),
                    );
                }
            }
        }
    }

    @observable
    public supportedNonDICOMFilesToArrange: FileUpload[] = [];

    @computed
    public get selectedNonDICOMFiles(): FileUpload[] {
        return this.supportedNonDICOMFilesToArrange.filter(f => f.selected);
    }

    @action
    private checkToArrangeSupportedNonDICOMFiles(): void {
        //if we only have 1 patient and 1 study, just add these files to that patient and study
        //if we have multiple patients and studies, let the user decide

        if (
            this.patientsFound.length === 1 &&
            this.patientsFound[0].studies.length === 1 &&
            this.patientsFound[0].studies[0].nonDicomFiles.length + this.supportedNonDICOMFilesToArrange.length <= this.maxNonDicomFilesAddedToAStudy
        ) {
            for (const file of this.supportedNonDICOMFilesToArrange) {
                file.setSelected(false);
                this.patientsFound[0].addNonDicomFileToStudy(file, this.patientsFound[0].studies[0]);
            }
            this.supportedNonDICOMFilesToArrange = [];
        }
    }

    @action
    private removeFileFromFilesToCheck(index: number) {
        this.filesToCheck.splice(index, 1);
    }

    @computed
    public get numberOfSelectedFiles(): number {
        return this.filesNotUploadable.length + this.filesUploadable.length + this.filesToCheck.length + this.numberOfFilesInPatients + this.supportedNonDICOMFilesToArrange.length;
    }

    //Will be used to temporarily store files that are ready to be checked in
    //In checkFilesToCheck() they are added to filesNotUploadable or filesUploadable
    @observable
    private filesToCheck: FileUpload[] = [];

    //Will be used to store files that will not be uploaded in
    @observable
    public filesNotUploadable: FileUpload[] = [];

    //Will be used to temporarily store files that are uploadable in
    //In checkFilesUploadable() they will be moved to the correct patients
    @observable
    private filesUploadable: FileUpload[] = [];

    @observable
    public patientsFound: PatientToUpload[] = [];

    @computed
    public get allStudies(): StudyInUpload[] {
        const allStudies: StudyInUpload[] = [];
        for (const p of this.patientsFound) {
            for (const s of p.studies) {
                allStudies.push(s);
            }
        }
        return allStudies;
    }

    @computed
    public get allStudiesThatAreNotUploadedYet(): StudyInUpload[] {
        const allStudiesThatAreNotUploadedYet: StudyInUpload[] = [];
        for (const p of this.patientsFound.filter(p => p.status == UploadStatus.NotStarted)) {
            for (const s of p.studies) {
                allStudiesThatAreNotUploadedYet.push(s);
            }
        }
        return allStudiesThatAreNotUploadedYet;
    }

    @computed
    public get numberOfDICOMFiles(): number {
        return this.numberOfDICOMFilesInPatients + this.filesUploadable.filter(s => s.fileIsADicomFile).length;
    }

    @computed
    public get numberOfNonDICOMFiles(): number {
        return this.numberOfNonDICOMFilesInPatients + this.filesUploadable.filter(s => !s.fileIsADicomFile).length + this.supportedNonDICOMFilesToArrange.length;
    }

    @computed
    private get numberOfDICOMFilesInPatients(): number {
        let totalFiles = 0;
        for (const patient of this.patientsFound) {
            totalFiles += patient.filesToUpload.filter(s => s.fileIsADicomFile).length;
        }
        return totalFiles;
    }

    @computed
    private get numberOfNonDICOMFilesInPatients(): number {
        let totalFiles = 0;
        for (const patient of this.patientsFound) {
            totalFiles += patient.filesToUpload.filter(s => !s.fileIsADicomFile).length;
        }
        return totalFiles;
    }

    @computed
    private get numberOfFilesInPatients(): number {
        let totalFiles = 0;
        for (const patient of this.patientsFound) {
            totalFiles += patient.filesToUpload.length;
        }
        return totalFiles;
    }

    @computed
    public get numberOfCheckingFiles(): number {
        return this.filesToCheck.length;
    }

    @computed
    public get preventInputOfNewFiles(): boolean {
        if (this.numberOfCheckingFiles > 0) return true; //Still checking selected files
        if (this.patientsFound?.some(s => s.status !== UploadStatus.NotStarted)) return true; //Already started or higher
        if (!this.uploadCodeAndSourceSelectionStore.uploadCodeAndSourceValid) return true;
        return false;
    }

    @computed
    public get allUploadsDone(): boolean {
        if (this.patientsFound.length > 0) {
            if (!this.patientsFound.some(s => s.status !== UploadStatus.Done)) {
                return true;
            }
        }
        return false;
    }

    @action
    private addFileToCheck(fileUpload: FileUpload) {
        if (!this.filesToCheck.some(f => f.file.name === fileUpload.file.name && f.file.size === fileUpload.file.size)) {
            this.filesToCheck.push(fileUpload);
        }
    }

    @action
    private addFileToNotUploadable(fileUpload: FileUpload) {
        if (!this.filesNotUploadable.some(f => f.file.name === fileUpload.file.name && f.file.size === fileUpload.file.size)) {
            this.filesNotUploadable.push(fileUpload);
        }
    }

    @action
    private addFileToUploadable(fileUpload: FileUpload) {
        if (!this.filesUploadable.some(f => f.file.name === fileUpload.file.name && f.file.size === fileUpload.file.size)) {
            this.filesUploadable.push(fileUpload);
        }
    }

    @action
    private makeNewSingleFileUpload(file: File): FileUpload {
        const { uploadCodeAndSourceSelectionStore } = this;
        const { selectedSourceForUpload, uploadCode } = uploadCodeAndSourceSelectionStore;

        if (nullOrUndefined(selectedSourceForUpload)) throw new Error("Cannot upload, no source is selected");

        return new FileUpload(file, this.endpoint, selectedSourceForUpload.id, uploadCode);
    }

    @action
    public resetFiles(): void {
        this.filesNotUploadable = [];
        this.filesUploadable = [];
        this.filesToCheck = [];
        this.patientsFound = [];
        this.supportedNonDICOMFilesToArrange = [];
    }

    @action
    public removePatientToUpload(patient: PatientToUpload): void {
        this.patientsFound = this.patientsFound.filter(p => p.patientGuid !== patient.patientGuid);
    }

    @computed
    public get somethingIsUploading(): boolean {
        return this.patientsFound.some(p => p.status === UploadStatus.Started);
    }

    @action
    public removeFileUpload(fileUpload: FileUpload): void {
        //FileUpload is a dicom file -> remove the file completely
        if (fileUpload.fileIsADicomFile) {
            const patientToUpload = this.patientsFound.find(s => s.patientId === fileUpload.patientId);
            if (patientToUpload) {
                patientToUpload.removeFile(fileUpload);
            }
        }
        //that is not assigned to a study yet -> just remove the file completely
        else if (isNullOrWhiteSpace(fileUpload.studyId)) {
            this.supportedNonDICOMFilesToArrange = this.supportedNonDICOMFilesToArrange.filter(f => f.fileGuid !== fileUpload.fileGuid);
        }
        //that was already assigned to a study -> remove from study and put it back in to arrange
        else {
            const patientToUpload = this.patientsFound.find(s => s.patientId === fileUpload.patientId);
            if (patientToUpload) {
                patientToUpload.removeFile(fileUpload);
            }
            fileUpload.setStudyIdForNonDicomFile("");
            fileUpload.setPatientId("");
            this.supportedNonDICOMFilesToArrange.push(fileUpload);
        }
    }

    private findPatientByStudy(study: StudyInUpload): PatientToUpload | null {
        let patientFound: PatientToUpload | null = null;
        for (const patient of this.patientsFound) {
            if (patient.studies.some(s => s.studyInstanceUid === study.studyInstanceUid)) {
                patientFound = patient;
            }
        }
        return patientFound;
    }

    @action
    public addAllSelectedNonDICOMFilesToArrangeToStudy(study: StudyInUpload): void {
        if (this.selectedNonDICOMFiles.length === 0) return;
        const patientToAddFileTo: PatientToUpload | null = this.findPatientByStudy(study);
        if (patientToAddFileTo) {
            //Check if the max of non-dicom files will not be reached
            if (study.nonDicomFiles.length + this.selectedNonDICOMFiles.length <= this.maxNonDicomFilesAddedToAStudy) {
                //Add non-dicom file to study
                for (const file of this.selectedNonDICOMFiles) {
                    file.setSelected(false);
                    patientToAddFileTo.addNonDicomFileToStudy(file, study);
                    this.supportedNonDICOMFilesToArrange = this.supportedNonDICOMFilesToArrange.filter(s => s.fileGuid !== file.fileGuid);
                }
            }
        }
    }

    @action
    public removeAllSelectedNonDICOMFiles(): void {
        if (this.selectedNonDICOMFiles.length === 0) return;
        for (const file of this.selectedNonDICOMFiles) {
            this.removeFileUpload(file);
        }
    }

    @computed
    public get reachedMaxUploadSize(): boolean {
        //No total size limitation for users
        if (this.currentUploadSettingsStore.userUploadsWithoutCode) return false;

        const { selectedSourceUploadSizeLimitInGb } = this.uploadCodeAndSourceSelectionStore;

        let totalSizeOfAllFiles = 0;
        for (const patient of this.patientsFound) {
            totalSizeOfAllFiles += patient.filesToUploadTotalSize;
        }

        return totalSizeOfAllFiles >= selectedSourceUploadSizeLimitInGb * 1_073_741_824;
    }
}
